import { useEffect, useMemo, useState } from 'react';
import {
  collection, doc, onSnapshot, orderBy, query,
} from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import { db, functions } from '../../lib/firebase.js';
import { useAuth } from '../../lib/auth.jsx';

// Phase 4 of v1 spec — admin Coverage calendar.
//
// Layout:
//   - Top summary strip: total red/yellow gaps in next 7 + 28 days, last computed time, recompute button.
//   - 28-day grid: rows = skill categories, cols = days. Cell color = red/yellow/green per vendor thresholds.
//   - Click any cell → side panel showing who's available, who's unavailable, who has no entry.
//   - "Add coverage on behalf of [responder]" button → admin-override modal.
//
// Data sources (all via onSnapshot — page stays fresh in real time):
//   - vendors/{vid}                                    → timezone, availabilityModel, thresholds
//   - vendors/{vid}/skillCategories                    → row labels
//   - vendors/{vid}/users (responders + admins)        → name lookup
//   - vendors/{vid}/coverageGaps                       → the precomputed grid
//   - vendors/{vid}/availability                       → for the side-panel breakdown
//
// Recompute:
//   Calls recomputeCoverageGaps callable. The coverageGaps onSnapshot fires immediately
//   after the batch write commits — UI updates without reloading.

const DAYS_TO_SHOW = 28;
const DAY_NAMES = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

export default function Coverage() {
  const { claims } = useAuth();
  const vendorId = claims?.vendor_id;

  const [vendor, setVendor] = useState(null);
  const [skills, setSkills] = useState([]);
  const [users, setUsers] = useState([]);              // for the override modal + name lookup
  const [coverageGaps, setCoverageGaps] = useState([]); // doc-id = 'YYYY-MM-DD'
  const [error, setError] = useState(null);
  const [busyRecompute, setBusyRecompute] = useState(false);

  // Drill-down state
  const [drilldown, setDrilldown] = useState(null); // { ymd, skillId } | null

  // Override modal state
  const [overrideTarget, setOverrideTarget] = useState(null); // uid string | null

  // ---- Subscriptions -------------------------------------------------------------

  useEffect(() => {
    if (!vendorId) return;
    const unsub = onSnapshot(
      doc(db, 'vendors', vendorId),
      (snap) => setVendor(snap.exists() ? snap.data() : {}),
      (err) => setError(err.message),
    );
    return () => unsub();
  }, [vendorId]);

  useEffect(() => {
    if (!vendorId) return;
    const unsub = onSnapshot(
      collection(db, 'vendors', vendorId, 'skillCategories'),
      (snap) => setSkills(snap.docs.map((d) => ({ id: d.id, ...d.data() }))),
      (err) => setError(err.message),
    );
    return () => unsub();
  }, [vendorId]);

  useEffect(() => {
    if (!vendorId) return;
    const unsub = onSnapshot(
      collection(db, 'vendors', vendorId, 'users'),
      (snap) => setUsers(snap.docs.map((d) => ({ id: d.id, ...d.data() }))),
      (err) => setError(err.message),
    );
    return () => unsub();
  }, [vendorId]);

  useEffect(() => {
    if (!vendorId) return;
    const unsub = onSnapshot(
      query(collection(db, 'vendors', vendorId, 'coverageGaps'), orderBy('date', 'asc')),
      (snap) => setCoverageGaps(snap.docs.map((d) => ({ id: d.id, ...d.data() }))),
      (err) => setError(err.message),
    );
    return () => unsub();
  }, [vendorId]);

  // ---- Derived state -------------------------------------------------------------

  const tz = vendor?.timezone || '';
  const gapsByYmd = useMemo(() => {
    const m = new Map();
    for (const g of coverageGaps) m.set(g.id, g);
    return m;
  }, [coverageGaps]);

  // Generate the next 28 day labels — same algorithm the backend uses, so the rows align.
  const dateLabels = useMemo(() => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const out = [];
    for (let i = 0; i < DAYS_TO_SHOW; i++) {
      const d = new Date(today);
      d.setDate(d.getDate() + i);
      out.push({
        date: d,
        ymd: ymdInTimezone(d, tz),
        dow: d.getDay(),
        dayNum: d.getDate(),
      });
    }
    return out;
  }, [tz]);

  // Top-summary aggregates: count red/yellow gaps in next 7 + next 28 days.
  const summary = useMemo(() => {
    const window7 = dateLabels.slice(0, 7).map((d) => d.ymd);
    const window28 = dateLabels.map((d) => d.ymd);
    const count = (window) =>
      window.reduce((acc, ymd) => {
        const g = gapsByYmd.get(ymd);
        const red = g?.gapsBySeverity?.red?.length || 0;
        const yellow = g?.gapsBySeverity?.yellow?.length || 0;
        acc.red += red;
        acc.yellow += yellow;
        return acc;
      }, { red: 0, yellow: 0 });
    const recent = coverageGaps[coverageGaps.length - 1];
    return {
      next7: count(window7),
      next28: count(window28),
      lastComputedAt: latestComputedAt(coverageGaps),
      hasAnyData: coverageGaps.length > 0,
    };
  }, [dateLabels, gapsByYmd, coverageGaps]);

  // ---- Actions -------------------------------------------------------------------

  async function handleRecompute() {
    setError(null);
    setBusyRecompute(true);
    try {
      await httpsCallable(functions, 'recomputeCoverageGaps')({ days: DAYS_TO_SHOW });
    } catch (err) {
      setError(err.message || String(err));
    } finally {
      setBusyRecompute(false);
    }
  }

  // ---- Render --------------------------------------------------------------------

  return (
    <>
      <div className="cov-header">
        <h2>Coverage</h2>
        <div className="cov-actions">
          <button
            className="btn-primary"
            onClick={handleRecompute}
            disabled={busyRecompute}
          >
            {busyRecompute ? 'Recomputing…' : '↻ Recompute now'}
          </button>
        </div>
      </div>

      {error && <div className="error-banner">{error}</div>}

      <SummaryStrip summary={summary} availabilityModel={vendor?.availabilityModel || 'opt-out'} />

      {!summary.hasAnyData && (
        <div className="placeholder">
          No coverage data yet. Click "Recompute now" above to generate the first
          snapshot. After that, the system recomputes nightly.
        </div>
      )}

      {/* Calendar grid */}
      {skills.length === 0 ? (
        <div className="placeholder" style={{ marginTop: 16 }}>
          No skill categories defined yet. Add some under "Skill Categories" first
          and they'll appear as rows here.
        </div>
      ) : (
        <CoverageGrid
          skills={skills}
          dateLabels={dateLabels}
          gapsByYmd={gapsByYmd}
          onCellClick={(ymd, skillId) => setDrilldown({ ymd, skillId })}
        />
      )}

      {/* Override section */}
      <section className="cov-override-section">
        <h3>Override availability on behalf of a responder</h3>
        <p className="cov-help">
          When a responder forgot to update their schedule (sick, on leave, etc.) you
          can write the entry on their behalf. It's audit-logged separately so the
          history shows it was an admin override.
        </p>
        <select
          value={overrideTarget || ''}
          onChange={(e) => setOverrideTarget(e.target.value || null)}
          className="cov-select"
        >
          <option value="">Choose a responder…</option>
          {users
            .filter((u) => ['responder', 'vendor_admin'].includes(u.role))
            .map((u) => (
              <option key={u.id} value={u.id}>{u.fullName || u.email}</option>
            ))
          }
        </select>
      </section>

      {/* Drill-down side panel */}
      {drilldown && (
        <DrilldownPanel
          ymd={drilldown.ymd}
          skill={skills.find((s) => s.id === drilldown.skillId)}
          gap={gapsByYmd.get(drilldown.ymd)}
          users={users}
          onClose={() => setDrilldown(null)}
          onOverride={(uid) => {
            setOverrideTarget(uid);
            setDrilldown(null);
          }}
        />
      )}

      {/* Override modal */}
      {overrideTarget && (
        <OverrideModal
          targetUid={overrideTarget}
          targetUser={users.find((u) => u.id === overrideTarget)}
          timezone={tz}
          onClose={() => setOverrideTarget(null)}
          onError={setError}
        />
      )}
    </>
  );
}

// ============================================================================
// SummaryStrip — top-of-page red/yellow counts + recompute timestamp
// ============================================================================

function SummaryStrip({ summary, availabilityModel }) {
  const { next7, next28, lastComputedAt, hasAnyData } = summary;
  return (
    <div className="cov-summary">
      <div className="cov-stat">
        <div className="cov-stat-num cov-stat-red">{next7.red}</div>
        <div className="cov-stat-label">red gaps · next 7 days</div>
      </div>
      <div className="cov-stat">
        <div className="cov-stat-num cov-stat-yellow">{next7.yellow}</div>
        <div className="cov-stat-label">yellow gaps · next 7 days</div>
      </div>
      <div className="cov-stat">
        <div className="cov-stat-num cov-stat-red">{next28.red}</div>
        <div className="cov-stat-label">red gaps · next 28 days</div>
      </div>
      <div className="cov-stat">
        <div className="cov-stat-num cov-stat-yellow">{next28.yellow}</div>
        <div className="cov-stat-label">yellow gaps · next 28 days</div>
      </div>
      <div className="cov-meta">
        <div>Last computed: {hasAnyData ? formatRelative(lastComputedAt) : 'never'}</div>
        <div>Model: <strong>{availabilityModel}</strong></div>
      </div>
    </div>
  );
}

// ============================================================================
// CoverageGrid — the calendar (skills × days)
// ============================================================================

function CoverageGrid({ skills, dateLabels, gapsByYmd, onCellClick }) {
  return (
    <div className="cov-grid-wrap">
      <table className="cov-grid">
        <thead>
          <tr>
            <th className="cov-grid-corner">Skill ↓ · Day →</th>
            {dateLabels.map((d) => (
              <th key={d.ymd} className={`cov-grid-day ${d.dow === 0 || d.dow === 6 ? 'is-weekend' : ''}`}>
                <div className="cov-grid-dow">{DAY_NAMES[d.dow]}</div>
                <div className="cov-grid-num">{d.dayNum}</div>
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {skills.map((skill) => (
            <tr key={skill.id}>
              <th className="cov-grid-skill">{skill.name}</th>
              {dateLabels.map((d) => {
                const gap = gapsByYmd.get(d.ymd);
                const cell = gap?.coveragePerSkill?.[skill.id];
                const severity = severityFor(skill.id, gap);
                return (
                  <td
                    key={d.ymd}
                    className={`cov-cell cov-cell-${severity}`}
                    onClick={() => onCellClick(d.ymd, skill.id)}
                    title={cell ? `${cell.available} available — ${(cell.names || []).join(', ') || 'nobody'}` : 'No data — recompute'}
                  >
                    {cell ? cell.available : '·'}
                  </td>
                );
              })}
            </tr>
          ))}
        </tbody>
      </table>
      <div className="cov-grid-legend">
        <span><span className="cov-dot cov-dot-red" /> red — under threshold</span>
        <span><span className="cov-dot cov-dot-yellow" /> yellow — at threshold</span>
        <span><span className="cov-dot cov-dot-green" /> green — well covered</span>
        <span style={{ color: 'var(--muted)' }}>· "·" means no data yet — click Recompute</span>
      </div>
    </div>
  );
}

// ============================================================================
// DrilldownPanel — side panel with who's available / unavailable for cell
// ============================================================================

function DrilldownPanel({ ymd, skill, gap, users, onClose, onOverride }) {
  if (!skill || !gap) {
    return (
      <div className="cov-drill" onClick={onClose}>
        <div className="cov-drill-card" onClick={(e) => e.stopPropagation()}>
          <button className="cov-drill-close" onClick={onClose}>×</button>
          <h3>{skill?.name || 'Skill'} · {ymd}</h3>
          <div className="placeholder" style={{ marginTop: 12 }}>
            No coverage data for this date yet. Recompute first.
          </div>
        </div>
      </div>
    );
  }

  const cell = gap.coveragePerSkill?.[skill.id];
  const availableNames = cell?.names || [];

  return (
    <div className="cov-drill" onClick={onClose}>
      <div className="cov-drill-card" onClick={(e) => e.stopPropagation()}>
        <button className="cov-drill-close" onClick={onClose}>×</button>
        <h3>{skill.name} · {ymd}</h3>
        <div className={`cov-drill-status cov-drill-status-${severityFor(skill.id, gap)}`}>
          {cell?.available || 0} available
        </div>

        <div className="cov-drill-section">
          <div className="cov-drill-section-title">Available</div>
          {availableNames.length === 0 ? (
            <div className="cov-drill-empty">Nobody available for this skill on this date.</div>
          ) : (
            <ul className="cov-drill-list">
              {availableNames.map((name) => <li key={name}>{name}</li>)}
            </ul>
          )}
        </div>

        <div className="cov-drill-section">
          <div className="cov-drill-section-title">Want to fix this gap?</div>
          <p className="cov-help" style={{ margin: '0 0 8px 0' }}>
            Pick a responder below to add availability on their behalf for this date.
          </p>
          <select
            className="cov-select"
            onChange={(e) => e.target.value && onOverride(e.target.value)}
            defaultValue=""
          >
            <option value="">Choose a responder…</option>
            {users
              .filter((u) => ['responder', 'vendor_admin'].includes(u.role))
              .map((u) => (
                <option key={u.id} value={u.id}>{u.fullName || u.email}</option>
              ))
            }
          </select>
        </div>
      </div>
    </div>
  );
}

// ============================================================================
// OverrideModal — admin writes availability on behalf of someone else
// Mirrors the responder Add modal but calls adminOverrideAvailability.
// Kept lean for the v1 admin surface.
// ============================================================================

function OverrideModal({ targetUid, targetUser, timezone, onClose, onError }) {
  const [status, setStatus] = useState('available');
  const [type, setType] = useState('one-off');
  const [startDate, setStartDate] = useState(todayYMD(timezone));
  const [endDate, setEndDate] = useState('');
  const [daysOfWeek, setDaysOfWeek] = useState([]);
  const [weekInterval, setWeekInterval] = useState(2);
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);
  const [localError, setLocalError] = useState(null);

  function toggleDay(d) {
    setDaysOfWeek((current) =>
      current.includes(d) ? current.filter((x) => x !== d) : [...current, d].sort((a, b) => a - b),
    );
  }

  async function save() {
    setLocalError(null);
    setBusy(true);
    try {
      const entry = {
        status,
        type,
        startDate: ymdToMidnightISO(startDate),
        endDate: endDate ? ymdToMidnightISO(endDate) : null,
        daysOfWeek: (type === 'weekly' || type === 'every-n-weeks') ? daysOfWeek : null,
        weekInterval: type === 'every-n-weeks' ? Number(weekInterval) : null,
        reason: reason.trim() || null,
      };
      await httpsCallable(functions, 'adminOverrideAvailability')({ targetUid, entry });
      onClose();
    } catch (err) {
      const msg = err?.message || String(err);
      setLocalError(msg);
      onError?.(msg);
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="ra-modal-backdrop" onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div className="ra-modal">
        <h3>Override availability</h3>
        <p className="settings-help" style={{ marginTop: -8 }}>
          For: <strong>{targetUser?.fullName || targetUser?.email || targetUid}</strong>.
          This change will be audit-logged as an admin override.
        </p>

        <div className="ra-field">
          <label>Status</label>
          <div className="ra-pills">
            <button type="button" className={`ra-pill ${status === 'available' ? 'is-active' : ''}`} onClick={() => setStatus('available')}>Available</button>
            <button type="button" className={`ra-pill ${status === 'unavailable' ? 'is-active' : ''}`} onClick={() => setStatus('unavailable')}>Unavailable</button>
          </div>
        </div>

        <div className="ra-field">
          <label>Repeats</label>
          <select value={type} onChange={(e) => setType(e.target.value)} className="ra-input">
            <option value="one-off">Doesn't repeat (one-off)</option>
            <option value="weekly">Weekly</option>
            <option value="every-n-weeks">Every N weeks</option>
          </select>
        </div>

        <div className="ra-field-row">
          <div className="ra-field">
            <label>Start date</label>
            <input type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)} className="ra-input" />
          </div>
          <div className="ra-field">
            <label>End date (optional)</label>
            <input type="date" value={endDate} onChange={(e) => setEndDate(e.target.value)} className="ra-input" />
          </div>
        </div>

        {(type === 'weekly' || type === 'every-n-weeks') && (
          <div className="ra-field">
            <label>Days of week</label>
            <div className="ra-dow">
              {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map((name, i) => (
                <button
                  key={name}
                  type="button"
                  className={`ra-dow-btn ${daysOfWeek.includes(i) ? 'is-active' : ''}`}
                  onClick={() => toggleDay(i)}
                >
                  {name}
                </button>
              ))}
            </div>
          </div>
        )}

        {type === 'every-n-weeks' && (
          <div className="ra-field">
            <label>Cadence</label>
            <select value={weekInterval} onChange={(e) => setWeekInterval(Number(e.target.value))} className="ra-input">
              <option value={2}>Every 2 weeks</option>
              <option value={3}>Every 3 weeks</option>
              <option value={4}>Every 4 weeks</option>
            </select>
          </div>
        )}

        <div className="ra-field">
          <label>Reason</label>
          <input type="text" value={reason} onChange={(e) => setReason(e.target.value)} className="ra-input" maxLength={200} placeholder="e.g. covering for sick colleague" />
        </div>

        {localError && <div className="error-banner">{localError}</div>}

        <div className="ra-modal-actions">
          <button className="btn-ghost" onClick={onClose} disabled={busy}>Cancel</button>
          <button className="btn-primary" onClick={save} disabled={busy}>
            {busy ? 'Saving…' : 'Save override'}
          </button>
        </div>
      </div>
    </div>
  );
}

// ============================================================================
// Helpers
// ============================================================================

function severityFor(skillId, gap) {
  if (!gap) return 'nodata';
  const sev = gap.gapsBySeverity || {};
  if ((sev.red || []).includes(skillId)) return 'red';
  if ((sev.yellow || []).includes(skillId)) return 'yellow';
  if ((sev.green || []).includes(skillId)) return 'green';
  return 'nodata';
}

function latestComputedAt(coverageGaps) {
  let max = 0;
  for (const g of coverageGaps) {
    const t = g.lastComputedAt?.toDate?.()?.getTime?.() || 0;
    if (t > max) max = t;
  }
  return max ? new Date(max) : null;
}

function formatRelative(date) {
  if (!date) return 'never';
  const diffMs = Date.now() - date.getTime();
  const mins = Math.round(diffMs / 60000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins} min ago`;
  const hours = Math.round(mins / 60);
  if (hours < 24) return `${hours} hr ago`;
  const days = Math.round(hours / 24);
  return `${days} day${days === 1 ? '' : 's'} ago`;
}

function ymdInTimezone(date, timezone) {
  const tz = timezone || undefined;
  try {
    return new Intl.DateTimeFormat('sv-SE', {
      timeZone: tz,
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
    }).format(date);
  } catch {
    return new Intl.DateTimeFormat('sv-SE', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
    }).format(date);
  }
}

function todayYMD(timezone) {
  return ymdInTimezone(new Date(), timezone);
}

function ymdToMidnightISO(ymd) {
  // Noon UTC, not midnight — see Availability.jsx for the long-form explanation.
  // (verification audit B2, 2026-05-15)
  return new Date(`${ymd}T12:00:00.000Z`).toISOString();
}
